'use client';

import React, { Suspense } from 'react';
import { Group, Badge, Container, Text, Loader } from '@mantine/core';
import { AnimatePresence, motion } from 'framer-motion';
import MentorGrid from '@/components/MentorGrid';
import MentorSearchHero from '@/components/mentorSearch/mentorSearchHero';
import AdvancedFiltersModal from '@/components/mentorSearch/advancedFiltersModal';
import { useMentorSearchStore } from '@/stores/useMentorSearchStore';
import FiltersSummary from '@/components/mentorSearch/filtersSummary';
import Section from '@/components/ui/Section';
import { useSearchParams } from 'next/navigation';
import { AnimatedGradientBackground } from '@/components/AnimatedGradientBackground';

// Component that handles URL search params
function MentorsSearchParamsHandler() {
  const searchParams = useSearchParams();
  const { setFilters, setModalValues, fetchMentors } = useMentorSearchStore();

  // Initialize filters from URL parameters
  React.useEffect(() => {
    const expertiseParam = searchParams.get('expertise');
    const nameParam = searchParams.get('name');
    const locationParam = searchParams.get('location');
    const badgeParam = searchParams.get('badge');
    
    const urlFilters: any = {};
    
    if (expertiseParam) {
      urlFilters.expertise = expertiseParam.split(',');
    }
    if (nameParam) {
      urlFilters.name = nameParam;
    }
    if (locationParam) {
      urlFilters.location = locationParam;
    }
    if (badgeParam) {
      urlFilters.badge = badgeParam.split(',');
    }
    
    // Always apply filters from URL (or clear them if no params)
    // This ensures data is loaded for scroll restoration on back navigation
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
      setModalValues(urlFilters);
      // Fire and forget - errors handled by store
      fetchMentors(true, urlFilters).catch(() => {});
    } else {
      // Clear filters when navigating to /mentors with no params
      setFilters({});
      setModalValues({
        expertise: [],
        location: '',
        distance: undefined,
        name: '',
        bio: '',
        job: '',
        timezone: '',
        skills: [],
      });
      // Fetch fresh data with no filters - errors handled by store
      fetchMentors(true, {}).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only run when URL params change, not when store functions change

  return null;
}

// Component to handle URL changes for scroll restoration
function ScrollRestorationHandler({ onUrlChange }: { onUrlChange: (urlKey: string) => void }) {
  const searchParams = useSearchParams();
  const currentUrlKey = searchParams.toString();
  
  React.useEffect(() => {
    onUrlChange(currentUrlKey);
  }, [currentUrlKey, onUrlChange]);
  
  return null;
}

export default function MentorsPage() {
  const { filters, users, isLoading } = useMentorSearchStore();
  const [scrollRestored, setScrollRestored] = React.useState(false);
  const [currentUrlKey, setCurrentUrlKey] = React.useState('');

  // Prevent Next.js automatic scroll restoration
  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Clear the profile tracking when on search page
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('lastMentorProfileHandle');
    }
  }, []);

  // Note: Scroll position is saved in MentorCard onClick to capture exact moment before navigation

  // Restore scroll position ONLY when coming back from mentor profile after data loads
  React.useEffect(() => {
    if (typeof window === 'undefined' || scrollRestored) return;

    // Don't do anything until data has loaded
    if (isLoading || !users || users.length === 0) return;

    const savedScrollY = sessionStorage.getItem('mentorSearchScrollY');
    const savedScrollUrl = sessionStorage.getItem('mentorSearchUrl');
    const cameFromSearch = sessionStorage.getItem('cameFromMentorSearch') === 'true';
    const currentUrl = window.location.pathname + window.location.search;

    if (cameFromSearch && savedScrollY && savedScrollUrl === currentUrl) {
      // Restore scroll position - wait for full DOM stability first
      const targetScroll = parseInt(savedScrollY, 10);
      
      // Wait for layout to settle, then aggressively restore
      setTimeout(() => {
        let attempts = 0;
        const maxAttempts = 60; // 3 seconds of polling
        let consecutiveMatches = 0;
        
        const interval = setInterval(() => {
          const current = window.scrollY;
          const diff = Math.abs(current - targetScroll);
          
          if (diff <= 5) {
            consecutiveMatches++;
            if (consecutiveMatches >= 3) {
              // Stable for 3 checks in a row
              clearInterval(interval);
              setScrollRestored(true);
              sessionStorage.removeItem('mentorSearchScrollY');
              sessionStorage.removeItem('mentorSearchUrl');
              sessionStorage.removeItem('cameFromMentorSearch');
            }
          } else {
            consecutiveMatches = 0;
            if (attempts < maxAttempts) {
              window.scrollTo({ top: targetScroll, behavior: 'instant' });
              attempts++;
            } else {
              clearInterval(interval);
              setScrollRestored(true);
              sessionStorage.removeItem('mentorSearchScrollY');
              sessionStorage.removeItem('mentorSearchUrl');
              sessionStorage.removeItem('cameFromMentorSearch');
            }
          }
        }, 50);
      }, 200); // Wait 200ms for initial layout
    } else {
      // Scroll to top for new navigation
      if (!scrollRestored) {
        window.scrollTo({ top: 0, behavior: 'instant' });
        setScrollRestored(true);
        // Clean up
        sessionStorage.removeItem('mentorSearchScrollY');
        sessionStorage.removeItem('mentorSearchUrl');
        sessionStorage.removeItem('cameFromMentorSearch');
      }
    }
  }, [isLoading, users, scrollRestored]);

  // Reset scroll restored flag when URL changes (new search)
  React.useEffect(() => {
    setScrollRestored(false);
  }, [currentUrlKey]);

  // Create a stable key based on search state to prevent flicker
  const searchKey = React.useMemo(() => {
    const filterKey = JSON.stringify(filters);
    const userCount = users?.length || 0;
    return `${filterKey}-${userCount}`;
  }, [filters, users?.length]);

  const handleUrlChange = React.useCallback((urlKey: string) => {
    setCurrentUrlKey(urlKey);
  }, []);

  return (
    <AnimatedGradientBackground isFullPage>
      <Section py="xl" contentWidth="lg" fullHeight>
        <Suspense fallback={null}>
          <MentorsSearchParamsHandler />
        </Suspense>
        <Suspense fallback={null}>
          <ScrollRestorationHandler onUrlChange={handleUrlChange} />
        </Suspense>
        <MentorSearchHero />
        <FiltersSummary filters={filters} />

        <div className="mt-10" style={{ marginTop: 30 }}>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Group justify="center" mt="xl">
                  <Loader size="lg" />
                </Group>
              </motion.div>
            ) : (
              <motion.div
                key={searchKey}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.3 }}
              >
                <MentorGrid users={users} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AdvancedFiltersModal />
      </Section>
    </AnimatedGradientBackground>
  );
}
